/* Device-local watchlist. Only product handles are persisted. */
(() => {
  'use strict';
  if (window.AOWishlistInitialized) return;
  const configNode = document.getElementById('ao-wishlist-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  window.AOWishlistInitialized = true;

  const text = config.strings;
  const storageKey = 'ao:wishlist:v1';
  const limit = 100;
  const handlePattern = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const root = new URL(config.rootUrl || '/', window.location.origin);
  if (root.origin !== window.location.origin) return;
  if (!root.pathname.endsWith('/')) root.pathname += '/';
  const products = new Map();
  const generations = new WeakMap();
  let handles = [];
  let storageFailed = false;
  let announcementTimer;
  let formatter;
  try { formatter = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }); }
  catch (_) { formatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }); }

  const validHandle = (value) => typeof value === 'string' && handlePattern.test(value);
  const normalize = (value) => Array.isArray(value) ? [...new Set(value.filter(validHandle))].slice(0, limit) : [];
  const route = (handle) => new URL(`products/${encodeURIComponent(handle)}`, root).href;
  const element = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = value;
    return node;
  };
  const setHidden = (node, hidden) => { if (node) node.hidden = hidden; };

  function readStorage() {
    try {
      const raw = window.localStorage.getItem(storageKey);
      handles = raw ? normalize(JSON.parse(raw)) : [];
      storageFailed = false;
    } catch (_) {
      handles = [];
      storageFailed = true;
    }
  }

  function announce(message, isError = false) {
    const live = document.getElementById('ao-wishlist-live');
    if (!live) return;
    clearTimeout(announcementTimer);
    live.hidden = false;
    live.dataset.error = String(isError);
    live.textContent = message;
    announcementTimer = window.setTimeout(() => { live.hidden = true; }, isError ? 12000 : 4500);
  }

  function syncControls() {
    document.querySelectorAll('[data-ao-wishlist-count]').forEach((node) => { node.textContent = String(handles.length); });
    document.querySelectorAll('[data-ao-wishlist-toggle]').forEach((button) => {
      const handle = button.dataset.productHandle;
      const saved = handles.includes(handle);
      button.disabled = !validHandle(handle);
      button.setAttribute('aria-pressed', String(saved));
      const title = button.dataset.productTitle || '';
      const actionLabel = (saved ? text.removeProduct : text.addProduct).replace('__PRODUCT__', title);
      button.setAttribute('aria-label', saved ? `${text.saved}. ${actionLabel}` : actionLabel);
      const label = button.querySelector('[data-ao-wishlist-label]');
      if (label) label.textContent = saved ? text.saved : text.add;
    });
    document.querySelectorAll('[data-ao-wishlist-storage-error]').forEach((node) => { node.hidden = !storageFailed; });
  }

  function save(next) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
      handles = next;
      storageFailed = false;
      syncControls();
      return true;
    } catch (_) {
      storageFailed = true;
      syncControls();
      announce(text.storageError, true);
      return false;
    }
  }

  function getProduct(handle) {
    if (!products.has(handle)) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      const request = fetch(`${route(handle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal
      }).then(async (response) => {
        if (response.status === 404) return { missing: true };
        if (!response.ok) throw new Error('Product request failed');
        const product = await response.json();
        if (!product || typeof product.title !== 'string' || !Number.isFinite(product.price)) throw new Error('Invalid product response');
        return { product };
      }).catch(() => ({ error: true })).finally(() => { window.clearTimeout(timeout); });
      products.set(handle, request);
    }
    return products.get(handle);
  }

  function removeButton(handle, title) {
    const button = element('button', 'ao2-wishlist__remove', text.remove);
    button.type = 'button';
    button.dataset.aoWishlistRemove = '';
    button.dataset.productHandle = handle;
    button.setAttribute('aria-label', text.removeProduct.replace('__PRODUCT__', title));
    return button;
  }

  function buildCard(handle, result) {
    const card = element('li', 'ao2-wishlist__card');
    card.dataset.aoWishlistItem = handle;
    const body = element('div', 'ao2-wishlist__body');
    const actions = element('div', 'ao2-wishlist__actions');
    if (!result.product) {
      const fallback = element('div', 'ao2-wishlist__image', text.noImage);
      body.append(element('h2', '', text.unavailableTitle));
      body.append(element('p', 'ao2-wishlist__problem', result.missing ? text.unavailableText : text.loadError));
      if (result.error) {
        const retry = element('button', 'ao2-wishlist__retry', text.retry);
        retry.type = 'button';
        retry.dataset.aoWishlistRetry = handle;
        actions.append(retry);
      }
      actions.append(removeButton(handle, text.unavailableTitle));
      card.append(fallback, body, actions);
      return card;
    }
    const product = result.product;
    const productUrl = route(handle);
    const imageLink = element('a', 'ao2-wishlist__image');
    imageLink.href = productUrl;
    imageLink.setAttribute('aria-label', product.title);
    let imageUrl;
    try {
      if (typeof product.featured_image === 'string' && product.featured_image) {
        imageUrl = new URL(product.featured_image, window.location.origin);
        if (imageUrl.protocol !== 'https:' || !(imageUrl.hostname === 'cdn.shopify.com' || imageUrl.origin === window.location.origin)) imageUrl = null;
      }
    } catch (_) { imageUrl = null; }
    if (imageUrl) {
      imageUrl.searchParams.set('width', '360');
      const image = element('img');
      image.src = imageUrl.href;
      image.alt = product.title;
      image.width = 360;
      image.height = 360;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => { imageLink.replaceChildren(document.createTextNode(text.noImage)); }, { once: true });
      imageLink.append(image);
    } else imageLink.textContent = text.noImage;
    const heading = element('h2');
    const titleLink = element('a', '', product.title);
    titleLink.href = productUrl;
    heading.append(titleLink);
    const amount = Number.isFinite(product.price_min) ? product.price_min : product.price;
    const price = element('p', 'ao2-wishlist__price', `${product.price_varies ? `${text.from} ` : ''}${formatter.format(amount / 100)}`);
    const availability = element('p', 'ao2-wishlist__availability', product.available ? text.available : text.soldOut);
    availability.dataset.available = String(Boolean(product.available));
    body.append(heading, price, availability);
    const view = element('a', 'ao2-wishlist__view', text.viewProduct);
    view.href = productUrl;
    actions.append(view, removeButton(handle, product.title));
    card.append(imageLink, body, actions);
    return card;
  }

  function focusHeading(page) {
    const heading = page.querySelector('h1');
    if (!heading) return;
    if (!heading.hasAttribute('tabindex')) {
      heading.setAttribute('tabindex', '-1');
      heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
    }
    heading.focus({ preventScroll: true });
  }

  function focusAfterRemoval(page, list, index) {
    const cards = [...list.children];
    const nextCard = cards[Math.max(0, Math.min(index, cards.length - 1))];
    const next = nextCard?.querySelector('[data-ao-wishlist-remove]') ||
      list.querySelector('[data-ao-wishlist-remove]') ||
      page.querySelector('[data-ao-wishlist-empty]:not([hidden]) a');
    if (next) next.focus({ preventScroll: true });
    else focusHeading(page);
  }

  function replaceCard(previous, next) {
    const active = document.activeElement;
    let selector;
    if (previous.contains(active)) {
      if (active.matches('[data-ao-wishlist-remove]')) selector = '[data-ao-wishlist-remove]';
      else if (active.matches('[data-ao-wishlist-retry]')) selector = '[data-ao-wishlist-retry], .ao2-wishlist__view, [data-ao-wishlist-remove]';
      else if (active.matches('.ao2-wishlist__view')) selector = '.ao2-wishlist__view, [data-ao-wishlist-remove]';
      else if (active.matches('.ao2-wishlist__image')) selector = '.ao2-wishlist__image[href], [data-ao-wishlist-remove]';
      else selector = 'h2 a, [data-ao-wishlist-remove]';
    }
    previous.replaceWith(next);
    if (selector) next.querySelector(selector)?.focus({ preventScroll: true });
  }

  async function renderPage(page, focusIndex, retryHandle, refresh = false) {
    const generation = (generations.get(page) || 0) + 1;
    generations.set(page, generation);
    const current = [...handles];
    const summary = page.querySelector('[data-ao-wishlist-summary]');
    if (summary) summary.textContent = current.length === 1 ? text.oneItem : text.manyItems.replace('__COUNT__', String(current.length));
    const list = page.querySelector('[data-ao-wishlist-list]');
    if (!list) return;
    setHidden(page.querySelector('[data-ao-wishlist-empty]'), current.length !== 0 || storageFailed);
    setHidden(page.querySelector('[data-ao-wishlist-storage-error]'), !storageFailed);
    const loading = page.querySelector('[data-ao-wishlist-loading]');
    const previous = new Map([...list.children].map((card) => [card.dataset.aoWishlistItem, card]));
    const focusedCard = document.activeElement?.closest('[data-ao-wishlist-item]');
    if (!Number.isInteger(focusIndex) && focusedCard && list.contains(focusedCard) && !current.includes(focusedCard.dataset.aoWishlistItem)) {
      focusIndex = [...list.children].indexOf(focusedCard);
    }
    previous.forEach((card, handle) => { if (!current.includes(handle)) card.remove(); });
    const pending = [];
    current.forEach((handle, index) => {
      let card = previous.get(handle);
      const needsProduct = !card || card.getAttribute('aria-busy') === 'true' || refresh || handle === retryHandle;
      if (!card) {
        card = element('li');
        card.dataset.aoWishlistItem = handle;
        card.hidden = true;
      }
      if (needsProduct) {
        card.setAttribute('aria-busy', 'true');
        card.querySelector('[data-ao-wishlist-retry]')?.setAttribute('aria-disabled', 'true');
        pending.push({ handle, card });
      }
      if (list.children[index] !== card) list.insertBefore(card, list.children[index] || null);
    });
    list.setAttribute('aria-busy', 'false');
    setHidden(loading, pending.length === 0);
    if (Number.isInteger(focusIndex)) focusAfterRemoval(page, list, focusIndex);
    if (retryHandle) pending.sort((a, b) => Number(b.handle === retryHandle) - Number(a.handle === retryHandle));
    let cursor = 0;
    async function worker() {
      while (cursor < pending.length) {
        const { handle, card } = pending[cursor++];
        const result = await getProduct(handle);
        if (generations.get(page) !== generation || !page.isConnected) return;
        if (!list.contains(card)) continue;
        replaceCard(card, buildCard(handle, result));
        setHidden(loading, !list.querySelector('[aria-busy="true"]'));
      }
    }
    await Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
  }

  function renderPages(focusPage, focusIndex, retryHandle, refresh = false) {
    document.querySelectorAll('[data-ao-wishlist-page]').forEach((page) => {
      renderPage(page, page === focusPage ? focusIndex : undefined, page === focusPage ? retryHandle : undefined, refresh);
    });
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const retry = event.target.closest('[data-ao-wishlist-retry]');
    if (retry) {
      const handle = retry.dataset.aoWishlistRetry;
      if (!validHandle(handle) || !handles.includes(handle) || retry.getAttribute('aria-disabled') === 'true') return;
      products.delete(handle);
      renderPages(retry.closest('[data-ao-wishlist-page]'), undefined, handle);
      return;
    }
    const button = event.target.closest('[data-ao-wishlist-toggle], [data-ao-wishlist-remove]');
    if (!button || button.disabled) return;
    const handle = button.dataset.productHandle;
    if (!validHandle(handle)) return;
    const removing = button.hasAttribute('data-ao-wishlist-remove') || handles.includes(handle);
    if (!removing && handles.length >= limit) { announce(text.limit, true); return; }
    const page = button.closest('[data-ao-wishlist-page]');
    const focusIndex = page && removing ? handles.indexOf(handle) : undefined;
    const next = removing ? handles.filter((item) => item !== handle) : [...handles, handle];
    if (!save(next)) return;
    announce(removing ? text.removed : text.added);
    renderPages(page, focusIndex);
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey && event.key !== null) return;
    readStorage();
    syncControls();
    renderPages();
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    readStorage();
    products.clear();
    syncControls();
    renderPages(undefined, undefined, undefined, true);
  });
  document.addEventListener('shopify:section:load', () => { syncControls(); renderPages(); });
  new MutationObserver((mutations) => {
    const hasNewToggle = mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
      node instanceof Element && (node.matches('[data-ao-wishlist-toggle]') || node.querySelector('[data-ao-wishlist-toggle]'))
    ));
    if (hasNewToggle) syncControls();
  }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
  readStorage();
  syncControls();
  renderPages();
})();
